import { geometricMean } from '../../financeMath';
import {
    AnyDebt,
    AnyFinancialEntity,
    getCurrentValue,
} from '../../financialEntities';
import LoanSeries, { makeLoanConfig } from '../../loanSeries';
import { AmountRecurrence } from '../../types';
import { DT } from '../../util/dateTime';
import { isNumber } from '../../util/types';
import { BaseValueNodeParams, SnapshotBase, Tick } from '../types';

export const makeDefaultPastValueNodeGenerator =
    <S extends SnapshotBase>({ id }: BaseValueNodeParams) =>
    ({ tick, snapshot }: { tick: Tick; snapshot?: S }) => {
        const balance = snapshot?.balance ?? null;
        return { ...tick, snapshot, balance, id };
    };

export const makeDefaultCurrentValueNodeGenerator =
    <S extends SnapshotBase>({
        currentValue,
        id,
    }: BaseValueNodeParams & {
        currentValue: number;
    }) =>
    ({ tick, snapshot }: { tick: Tick; snapshot?: S }) => {
        return { ...tick, snapshot, balance: currentValue, id };
    };

type LoanParams = {
    currentValue: number;
    interestRate: number;
    payment: number;
};

export const defaultLoanEntityParams = <E extends AnyDebt>(
    entity: E,
    params: {
        balance?: number;
        interestRate?: number;
        currentPayment?: number;
        plannedPayment?: AmountRecurrence;
    }
): LoanParams => {
    const currentValue = isNumber(params.balance)
        ? params.balance
        : getCurrentValue(entity) ?? 0;
    const interestRate = isNumber(params.interestRate)
        ? params.interestRate
        : entity.info.interestRate ?? 0;
    const payment =
        params.plannedPayment?.amount ??
        params.currentPayment ??
        entity.info.plannedPayment?.amount ??
        entity.info.currentPayment ??
        entity.info.minimumPayment ??
        0;

    return {
        currentValue,
        interestRate: geometricMean(interestRate, 12),
        payment,
    };
};

export const loanSeriesFromDefaultLoanParams = (
    { currentValue, payment, interestRate }: LoanParams,
    currentDate: string | DT
): LoanSeries => {
    const loanConfig = makeLoanConfig({
        amount: currentValue,
        payment,
        interestRate,
    });
    return new LoanSeries(currentDate, loanConfig);
};

export const defaultValueNodeParams = (
    fe: AnyFinancialEntity
): BaseValueNodeParams => ({
    id: fe.id,
});
